import { useState, useEffect, useCallback } from 'react';
import { Loader2, Save, Info, Plus, Trash2, Check, Eye, Send, ShieldAlert } from 'lucide-react';
import { arDunningApi } from '../../services/arDunningApi';
import { useToast } from '../ui/Toast';
import { useAuth } from '../../contexts/AuthContext';
import type { ArConfig, ArRecipient, ArTemplate, ArTemplateAudience, ArTemplateLanguage, ArDigestResult } from '../../types/arDunning';

interface Props {
    canEdit: boolean;
}

const AUDIENCES: ArTemplateAudience[] = ['Internal', 'Customer'];
const LANGS: ArTemplateLanguage[] = ['EN', 'DE', 'FR', 'IT'];
const LEVELS = [1, 2, 3];
const PLACEHOLDERS = '{CustomerName} · {CompanyName} · {InvoiceNo} · {InvoiceList} · {OpenAmount} · {TotalOpenAmount} · {Currency} · {DaysOverdue} · {SenderName} · {SellerName} · {AppUrl}';

const tplKey = (level: number, audience: string, lang: string) => `${level}|${audience}|${lang}`;

export function ArSettingsTab({ canEdit }: Props) {
    const { toast } = useToast();
    const { user } = useAuth();
    const isAdmin = user?.isAdmin ?? false;
    const [loading, setLoading] = useState(true);
    const [config, setConfig] = useState<ArConfig | null>(null);
    const [recipients, setRecipients] = useState<ArRecipient[]>([]);
    const [templates, setTemplates] = useState<ArTemplate[]>([]);
    const [savingCfg, setSavingCfg] = useState(false);

    // Digest preview / test (admin)
    const [digestBusy, setDigestBusy] = useState<null | 'preview' | 'test'>(null);
    const [digest, setDigest] = useState<ArDigestResult | null>(null);

    // Template editor state
    const [editKey, setEditKey] = useState<string | null>(null);
    const [tLevel, setTLevel] = useState(1);
    const [tAudience, setTAudience] = useState<ArTemplateAudience>('Internal');
    const [tLang, setTLang] = useState<ArTemplateLanguage>('EN');
    const [tSubject, setTSubject] = useState('');
    const [tBody, setTBody] = useState('');
    const [savingTpl, setSavingTpl] = useState(false);

    // Recipient editor state
    const [newRecipient, setNewRecipient] = useState('');
    const [newCentral, setNewCentral] = useState(true);
    const [savingRcp, setSavingRcp] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [cfg, tpl] = await Promise.all([arDunningApi.getConfig(), arDunningApi.listTemplates()]);
            setConfig(cfg.config);
            setRecipients(cfg.recipients);
            setTemplates(tpl.templates);
        } catch (e) {
            toast(e instanceof Error ? e.message : 'Failed to load settings', 'error');
        } finally {
            setLoading(false);
        }
    }, [toast]);

    useEffect(() => { void load(); }, [load]);

    const saveConfig = async () => {
        if (!config) return;
        if (!(config.level1OffsetDays <= config.level2OffsetDays && config.level2OffsetDays <= config.level3OffsetDays)) {
            toast('Offsets must be non-decreasing (L1 ≤ L2 ≤ L3)', 'info');
            return;
        }
        setSavingCfg(true);
        try {
            const res = await arDunningApi.updateConfig(config);
            setConfig(res.config);
            toast('Cadence saved', 'success');
        } catch (e) {
            toast(e instanceof Error ? e.message : 'Failed to save', 'error');
        } finally {
            setSavingCfg(false);
        }
    };

    const templateAt = (level: number, audience: string, lang: string): ArTemplate | undefined =>
        templates.find(t => t.DunningLevel === level && t.Audience === audience && t.LanguageCode === lang);

    const openCell = (level: number, audience: ArTemplateAudience, lang: ArTemplateLanguage) => {
        const existing = templateAt(level, audience, lang);
        setEditKey(tplKey(level, audience, lang));
        setTLevel(level); setTAudience(audience); setTLang(lang);
        setTSubject(existing?.Subject ?? '');
        setTBody(existing?.BodyHtml ?? '');
    };
    const cancelTemplate = () => setEditKey(null);

    const saveTemplate = async () => {
        if (!tSubject.trim() || !tBody.trim()) { toast('Subject and body are required', 'info'); return; }
        setSavingTpl(true);
        try {
            await arDunningApi.upsertTemplate({ dunningLevel: tLevel, audience: tAudience, languageCode: tLang, subject: tSubject.trim(), bodyHtml: tBody });
            toast('Template saved', 'success');
            setEditKey(null);
            await load();
        } catch (e) {
            toast(e instanceof Error ? e.message : 'Failed to save template', 'error');
        } finally {
            setSavingTpl(false);
        }
    };

    const addRecipient = async () => {
        const email = newRecipient.trim();
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { toast('Enter a valid e-mail', 'info'); return; }
        setSavingRcp(true);
        try {
            await arDunningApi.upsertRecipient({ userEmail: email, isCentral: newCentral, digestOptIn: true });
            setNewRecipient('');
            await load();
            toast('Recipient saved', 'success');
        } catch (e) {
            toast(e instanceof Error ? e.message : 'Failed to save recipient', 'error');
        } finally {
            setSavingRcp(false);
        }
    };
    const toggleRecipient = async (r: ArRecipient, patch: { isCentral?: boolean; digestOptIn?: boolean }) => {
        try {
            await arDunningApi.upsertRecipient({ userEmail: r.UserEmail, isCentral: patch.isCentral ?? r.IsCentral, digestOptIn: patch.digestOptIn ?? r.DigestOptIn });
            await load();
        } catch (e) {
            toast(e instanceof Error ? e.message : 'Failed to update recipient', 'error');
        }
    };
    const removeRecipient = async (r: ArRecipient) => {
        try {
            await arDunningApi.deleteRecipient(r.Id);
            await load();
        } catch (e) {
            toast(e instanceof Error ? e.message : 'Failed to remove recipient', 'error');
        }
    };

    const runPreview = async () => {
        setDigestBusy('preview');
        try {
            const res = await arDunningApi.previewDigest();
            setDigest(res);
            toast('Digest preview computed (no mail sent)', 'success');
        } catch (e) {
            toast(e instanceof Error ? e.message : 'Failed to preview digest', 'error');
        } finally {
            setDigestBusy(null);
        }
    };
    const runTest = async () => {
        setDigestBusy('test');
        try {
            const res = await arDunningApi.sendTestDigest();
            setDigest(res);
            if (res.skipped) toast(`Test digest skipped: ${res.reason ?? 'nothing to send'}`, 'info');
            else if (res.sent > 0) toast('Test digest sent to roman.dill@dolder.com', 'success');
            else toast(`Test digest not sent (${res.failed} failed)`, 'error');
        } catch (e) {
            toast(e instanceof Error ? e.message : 'Failed to send test digest', 'error');
        } finally {
            setDigestBusy(null);
        }
    };

    if (loading) return <div className="py-10 text-center text-slate-400"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></div>;

    return (
        <div className="space-y-4">
            <div className="flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
                <Info className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
                <span>Dunning e-mails are sent as the logged-in user. Level 1 is routed internally to the sales manager; levels 2 &amp; 3 go to the customer (sales manager in CC; central credit control in CC on L3). Tune the cadence, edit the full template matrix, and manage central controllers here.</span>
            </div>

            <div className="grid gap-4 lg:grid-cols-2">
                {/* Cadence & digest */}
                {config && (
                    <div className="rounded-lg border border-slate-200 bg-white p-4">
                        <h3 className="mb-3 text-sm font-semibold text-slate-700">Dunning cadence &amp; digest</h3>
                        <div className="grid grid-cols-3 gap-3">
                            {([['level1OffsetDays', 'L1 after (days)'], ['level2OffsetDays', 'L2 after (days)'], ['level3OffsetDays', 'L3 after (days)']] as const).map(([field, label]) => (
                                <label key={field} className="block text-xs font-medium text-slate-500">{label}
                                    <input type="number" min={0} max={3650} disabled={!canEdit} value={config[field]} onChange={e => setConfig({ ...config, [field]: Number(e.target.value) })} className="mt-1 w-full rounded border border-slate-300 px-2 py-1.5 text-sm disabled:bg-slate-50" />
                                </label>
                            ))}
                        </div>
                        <div className="mt-3 space-y-2 text-sm">
                            <label className="flex items-center gap-2 text-slate-600">
                                <input type="checkbox" disabled={!canEdit} checked={config.autoEscalationEnabled} onChange={e => setConfig({ ...config, autoEscalationEnabled: e.target.checked })} />
                                Auto-escalation <span className="text-xs text-slate-400">(display only — highlights items due for L2/L3 in the digest; never writes, never sends)</span>
                            </label>
                            <label className="flex items-center gap-2 text-slate-600">
                                <input type="checkbox" disabled={!canEdit} checked={config.digestEnabled} onChange={e => setConfig({ ...config, digestEnabled: e.target.checked })} />
                                Daily digest <span className="text-xs text-slate-400">(scheduled internal overview)</span>
                            </label>
                            <label className="flex items-center gap-2 text-slate-600">Digest hour (CET)
                                <input type="number" min={0} max={23} disabled={!canEdit} value={config.digestHourCet} onChange={e => setConfig({ ...config, digestHourCet: Number(e.target.value) })} className="w-16 rounded border border-slate-300 px-2 py-1 text-sm disabled:bg-slate-50" />
                            </label>
                        </div>
                        {canEdit && (
                            <button onClick={saveConfig} disabled={savingCfg} className="mt-4 inline-flex items-center gap-1 rounded-md bg-slate-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50">
                                {savingCfg ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save cadence
                            </button>
                        )}
                    </div>
                )}

                {/* Central controllers */}
                <div className="rounded-lg border border-slate-200 bg-white p-4">
                    <h3 className="mb-3 text-sm font-semibold text-slate-700">Central controllers &amp; digest recipients</h3>
                    <p className="mb-2 text-xs text-slate-400">Central controllers see all customers and are CC'd on Level-3 final notices.</p>
                    {recipients.length === 0 ? (
                        <p className="text-sm text-slate-400">None configured. Central access defaults to admins and non-sales-manager users.</p>
                    ) : (
                        <div className="space-y-1">
                            {recipients.map(r => (
                                <div key={r.Id} className="flex items-center justify-between rounded border border-slate-100 px-3 py-1.5 text-sm">
                                    <span className="truncate text-slate-700">{r.UserEmail}</span>
                                    <span className="flex items-center gap-1">
                                        <button disabled={!canEdit} onClick={() => toggleRecipient(r, { isCentral: !r.IsCentral })} className={`rounded-full px-2 py-0.5 text-xs font-medium ${r.IsCentral ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-100 text-slate-400'} disabled:opacity-60`}>central</button>
                                        <button disabled={!canEdit} onClick={() => toggleRecipient(r, { digestOptIn: !r.DigestOptIn })} className={`rounded-full px-2 py-0.5 text-xs font-medium ${r.DigestOptIn ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-400'} disabled:opacity-60`}>digest</button>
                                        {canEdit && <button onClick={() => removeRecipient(r)} className="rounded p-1 text-slate-400 hover:bg-rose-50 hover:text-rose-600"><Trash2 className="h-3.5 w-3.5" /></button>}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                    {canEdit && (
                        <div className="mt-3 flex flex-wrap items-center gap-2">
                            <input value={newRecipient} onChange={e => setNewRecipient(e.target.value)} placeholder="name@dolder.com" className="min-w-[180px] flex-1 rounded border border-slate-300 px-2 py-1.5 text-sm" />
                            <label className="flex items-center gap-1 text-xs text-slate-600"><input type="checkbox" checked={newCentral} onChange={e => setNewCentral(e.target.checked)} /> central</label>
                            <button onClick={addRecipient} disabled={savingRcp} className="inline-flex items-center gap-1 rounded-md bg-slate-700 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-slate-800 disabled:opacity-50">{savingRcp ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Add</button>
                        </div>
                    )}
                </div>
            </div>

            {/* Daily digest — preview & test */}
            <div className="rounded-lg border border-slate-200 bg-white p-4">
                <h3 className="mb-1 text-sm font-semibold text-slate-700">Daily digest &mdash; preview &amp; test</h3>
                <p className="mb-3 text-xs text-slate-500">
                    An internal action-list (reminders due, promises &amp; follow-ups due, newly overdue) mailed to central controllers (firm-wide) and each sales manager (their customers). It never contacts customers and never sends reminders &mdash; those stay manual. The scheduler runs daily at the digest hour when &ldquo;Daily digest&rdquo; is enabled.
                </p>
                <div className="mb-3 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                    <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
                    <span>Safety: test mode is active until explicitly disabled &mdash; every digest mail is redirected to <strong>roman.dill@dolder.com</strong>. &ldquo;Send test digest&rdquo; delivers one firm-wide digest to Roman only.</span>
                </div>
                {isAdmin ? (
                    <div className="flex flex-wrap gap-2">
                        <button onClick={runPreview} disabled={digestBusy !== null} className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50">
                            {digestBusy === 'preview' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />} Preview (no mail)
                        </button>
                        <button onClick={runTest} disabled={digestBusy !== null} className="inline-flex items-center gap-1 rounded-md bg-slate-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50">
                            {digestBusy === 'test' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send test digest to me
                        </button>
                    </div>
                ) : (
                    <p className="text-xs text-slate-400">Preview and test are available to admins.</p>
                )}

                {digest && (
                    <div className="mt-4 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm">
                        <p className="mb-2 text-xs text-slate-500">
                            {digest.today} · {digest.mode === 'preview' ? 'Preview (dry-run)' : 'Test send'}
                            {digest.testOnly ? ' · test mode (redirected to Roman)' : ' · LIVE distribution'}
                            {digest.reason ? ` · ${digest.reason}` : ''}
                        </p>
                        {digest.central && (
                            <div className="mb-2">
                                <span className="font-medium text-slate-700">Firm-wide:</span>{' '}
                                <span className="text-slate-600">
                                    {digest.central.remindersCount} reminders due (L1 {digest.central.byLevel.l1} / L2 {digest.central.byLevel.l2} / L3 {digest.central.byLevel.l3}) ·
                                    {' '}{digest.central.promisesCount} promises · {digest.central.followUpsCount} follow-ups · {digest.central.watchCount} newly overdue
                                </span>
                            </div>
                        )}
                        <div className="text-slate-600">
                            <span className="font-medium text-slate-700">Sellers with actionable items:</span> {digest.sellers.length}
                            {digest.sellers.length > 0 && (
                                <ul className="mt-1 max-h-40 space-y-0.5 overflow-y-auto text-xs">
                                    {digest.sellers.slice(0, 30).map(s => (
                                        <li key={s.email} className="truncate">
                                            {s.name || s.email} — {s.counts.remindersCount} reminders, {s.counts.promisesCount} promises, {s.counts.followUpsCount} follow-ups
                                        </li>
                                    ))}
                                    {digest.sellers.length > 30 && <li className="text-slate-400">… and {digest.sellers.length - 30} more</li>}
                                </ul>
                            )}
                        </div>
                        {digest.mode !== 'preview' && (
                            <p className="mt-2 text-xs text-slate-500">Sent {digest.sent} · failed {digest.failed}{digest.escalationsSuggested ? ` · ${digest.escalationsSuggested} due for L2/L3 (shown only)` : ''}. Verify in the Dunning History / EmailLog.</p>
                        )}
                    </div>
                )}
            </div>

            {/* Template matrix */}
            <div className="rounded-lg border border-slate-200 bg-white p-4">
                <div className="mb-3 flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-slate-700">E-mail template matrix <span className="text-xs font-normal text-slate-400">(level × audience × language — {templates.length}/24)</span></h3>
                </div>

                {editKey !== null ? (
                    <div className="space-y-3">
                        <div className="grid grid-cols-3 gap-3">
                            <label className="block text-xs font-medium text-slate-500">Level
                                <select value={tLevel} onChange={e => setTLevel(Number(e.target.value))} className="mt-1 w-full rounded border border-slate-300 px-2 py-1.5 text-sm">{LEVELS.map(l => <option key={l} value={l}>L{l}</option>)}</select>
                            </label>
                            <label className="block text-xs font-medium text-slate-500">Audience
                                <select value={tAudience} onChange={e => setTAudience(e.target.value as ArTemplateAudience)} className="mt-1 w-full rounded border border-slate-300 px-2 py-1.5 text-sm">{AUDIENCES.map(a => <option key={a} value={a}>{a}</option>)}</select>
                            </label>
                            <label className="block text-xs font-medium text-slate-500">Language
                                <select value={tLang} onChange={e => setTLang(e.target.value as ArTemplateLanguage)} className="mt-1 w-full rounded border border-slate-300 px-2 py-1.5 text-sm">{LANGS.map(l => <option key={l} value={l}>{l}</option>)}</select>
                            </label>
                        </div>
                        <label className="block text-xs font-medium text-slate-500">Subject
                            <input value={tSubject} onChange={e => setTSubject(e.target.value)} className="mt-1 w-full rounded border border-slate-300 px-2 py-1.5 text-sm" />
                        </label>
                        <label className="block text-xs font-medium text-slate-500">Body (HTML)
                            <textarea value={tBody} onChange={e => setTBody(e.target.value)} rows={8} className="mt-1 w-full rounded border border-slate-300 px-2 py-1.5 font-mono text-xs" />
                        </label>
                        <p className="text-xs text-slate-400">Placeholders: {PLACEHOLDERS}</p>
                        <div className="flex justify-end gap-2">
                            <button onClick={cancelTemplate} className="rounded-md border px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50">Cancel</button>
                            <button onClick={saveTemplate} disabled={savingTpl} className="inline-flex items-center gap-1 rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50">{savingTpl ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save template</button>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {LEVELS.map(level => (
                            <div key={level}>
                                <h4 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">Level {level}</h4>
                                <div className="overflow-x-auto">
                                    <table className="min-w-full border-separate border-spacing-1 text-sm">
                                        <thead>
                                            <tr className="text-xs uppercase tracking-wide text-slate-400">
                                                <th className="px-2 py-1 text-left font-medium">Audience</th>
                                                {LANGS.map(l => <th key={l} className="px-2 py-1 text-center font-medium">{l}</th>)}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {AUDIENCES.map(audience => (
                                                <tr key={audience}>
                                                    <td className="px-2 py-1 text-slate-600">{audience}</td>
                                                    {LANGS.map(lang => {
                                                        const exists = !!templateAt(level, audience, lang);
                                                        return (
                                                            <td key={lang} className="px-1 py-1 text-center">
                                                                <button
                                                                    onClick={() => canEdit ? openCell(level, audience, lang) : undefined}
                                                                    disabled={!canEdit && !exists}
                                                                    title={exists ? 'Edit template' : 'Add template'}
                                                                    className={`inline-flex w-full items-center justify-center gap-1 rounded border px-2 py-1 text-xs font-medium ${exists ? 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100' : 'border-dashed border-slate-300 text-slate-400 hover:bg-slate-50'} ${!canEdit ? 'cursor-default' : ''}`}
                                                                >
                                                                    {exists ? <><Check className="h-3 w-3" /> {canEdit ? 'Edit' : 'Set'}</> : <><Plus className="h-3 w-3" /> Add</>}
                                                                </button>
                                                            </td>
                                                        );
                                                    })}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        ))}
                        <p className="text-xs text-slate-400">Placeholders: {PLACEHOLDERS}</p>
                    </div>
                )}
            </div>
        </div>
    );
}
